import { API_URL } from '../../constants';
import { deleteRequest, getRequest, postRequest } from '../../utils/requestService';
import { getDispatch } from '../../../spa/store';

// mirrors backend/src/orm/entities/cefrLevel.ts
export const CEFR_LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'] as const;
export type TCefrLevel = typeof CEFR_LEVELS[number];

// mirrors backend/src/orm/entities/Exercise.ts and ExerciseAnswer.ts
export type TExerciseChoiceItem = {
	context?: string;
	question: string;
	choices: string[];
	answer: number;
	explanation: string;
	timestamp?: number;
	quote?: string;
};

export type TExerciseMatchItem = {
	pairs: { word: string; meaning: string }[];
};

export type TExerciseContent = {
	// the format it was generated in; missing on exercises generated before it was stored
	format?: 'choice' | 'match';
	instruction?: string;
	items: TExerciseChoiceItem[] | TExerciseMatchItem[];
	// missing on exercises generated before levels existed
	level?: TCefrLevel;
	generatedAt?: string;
};

// mirrors backend TExerciseNative: items are index-aligned with choice items, sets with a match exercise's pairs
export type TExerciseNative = {
	instruction?: string;
	items: { question: string; explanation: string }[];
	sets?: { wordNative: string; meaningNative: string }[][];
};

export type TExerciseChoiceAnswer = { choice: number };
// order[wordIndex] = the meaning index the student paired it with
export type TExerciseMatchAnswer = { order: number[] };
export type TExerciseAnswerValue = TExerciseChoiceAnswer | TExerciseMatchAnswer;

export type TExerciseFormat = 'choice' | 'match';

export interface IRestExerciseType {
	promptId: number;
	key: string;
	title: string;
	format: TExerciseFormat;
}

// mirrors backend EExerciseStatus: INACTIVE = taken out of the lesson by a teacher/admin
export enum EExerciseStatus {
	INACTIVE = 0,
	ACTIVE = 1,
}

export interface IRestExercise {
	id: number;
	promptId: number;
	content: TExerciseContent;
	status: EExerciseStatus;
}

export interface IRestExerciseAnswer {
	exerciseId: number;
	questionIndex: number;
	answer: TExerciseAnswerValue;
	correct: boolean;
}

export interface IRestExercisesData {
	types: IRestExerciseType[];
	exercises: IRestExercise[];
	myAnswers: IRestExerciseAnswer[];
	canRegenerate: boolean;
	// asked with lang: stored translations by exercise id; one missing is translated via fetchNative
	natives?: Record<number, TExerciseNative>;
}

export interface IRestExerciseStudentAnswer extends IRestExerciseAnswer {
	userId: number;
}

export interface IRestExerciseStudent {
	userId: number;
	name: string;
}

export interface IRestExerciseStudentsData {
	students: IRestExerciseStudent[];
	answers: IRestExerciseStudentAnswer[];
}

export class ExercisesRest {

	private static URL = API_URL + '/audio/exercises';

	// langCode: the viewer's native language, for the stored translations
	public static async fetch(audioId: number, langCode?: string): Promise<IRestExercisesData> {
		const dispatch = getDispatch();
		return await dispatch(getRequest(`${ExercisesRest.URL}/${audioId}${langCode ? `?lang=${encodeURIComponent(langCode)}` : ''}`));
	}

	// level: teacher/admin regenerate; omitted = the lesson's current level
	public static async generate(audioId: number, promptId: number, level?: TCefrLevel): Promise<{ id: number, content: TExerciseContent }> {
		const dispatch = getDispatch();
		return await dispatch(postRequest(`${ExercisesRest.URL}/${audioId}/generate`, { promptId, ...(level && { level }) }));
	}

	// native null — the lesson is in that language; content — what the translation was made from
	public static async fetchNative(exerciseId: number, langCode: string): Promise<{ native: TExerciseNative | null, content: TExerciseContent }> {
		const dispatch = getDispatch();
		return await dispatch(getRequest(`${ExercisesRest.URL}/native/${exerciseId}?lang=${encodeURIComponent(langCode)}`));
	}

	public static async saveAnswer(exerciseId: number, questionIndex: number, answer: TExerciseAnswerValue): Promise<{ correct: boolean }> {
		const dispatch = getDispatch();
		return await dispatch(postRequest(`${ExercisesRest.URL}/answer`, { exerciseId, questionIndex, answer }));
	}

	// userId: a participant's answers (teacher/admin); omitted = the caller's own
	public static async setStatus(exerciseId: number, status: EExerciseStatus): Promise<void> {
		const dispatch = getDispatch();
		await dispatch(postRequest(`${ExercisesRest.URL}/status/${exerciseId}`, { status }));
	}

	public static async clearAnswers(exerciseId: number, userId?: number): Promise<void> {
		const dispatch = getDispatch();
		await dispatch(deleteRequest(`${ExercisesRest.URL}/answers/${exerciseId}${userId ? `?userId=${userId}` : ''}`));
	}

	public static async fetchStudents(audioId: number): Promise<IRestExerciseStudentsData> {
		const dispatch = getDispatch();
		return await dispatch(getRequest(`${ExercisesRest.URL}/${audioId}/students`));
	}

}
